import { BehaviorSubject } from 'rxjs';
import { addError } from './Error';
import {
  getProjectContacts,
  deleteProjectContact as apiDeleteProjectContact,
  postProjectContacts
} from '../Api/Projects/projectContactsApi';

export enum ProjectContactType {
  ON_SITE = 'On Site',
  SUPPLIER = 'Supplier',
  UPSTACK = 'UPSTACK'
}

export interface ProjectContact {
  id: number;
  projectId: string;
  assetId: string;
  name: string;
  email: string;
  phone: string;
  company: string | null;
  type: ProjectContactType;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isProjectContact = (contact: any): contact is ProjectContact => {
  return (
    !!contact &&
    typeof contact.id === 'number' &&
    typeof contact.projectId === 'string' &&
    typeof contact.assetId === 'string' &&
    typeof contact.name === 'string' &&
    typeof contact.email === 'string' &&
    typeof contact.phone === 'string' &&
    (typeof contact.company === 'string' || contact.company === null) &&
    Object.values(ProjectContactType).includes(contact.type)
  );
};

const defaultProjectContacts: ProjectContact[] = [];
const projectContactSubject = new BehaviorSubject<ProjectContact[]>(defaultProjectContacts);
const projectContactLoadingSubject = new BehaviorSubject<boolean>(false);
const emitErrorState = (message?: string): void => addError('project-contact', message);

export const getProjectContactSubject = (): BehaviorSubject<ProjectContact[]> => projectContactSubject;
export const getProjectContactLoadingSubject = (): BehaviorSubject<boolean> => projectContactLoadingSubject;

export const fetchProjectContacts = async (
  projectId: string,
  assetId: string,
  forceRefresh: boolean = false
): Promise<void> => {
  if (projectContactLoadingSubject.getValue() || (!forceRefresh && projectContactSubject.getValue().length > 0)) {
    return;
  }

  projectContactLoadingSubject.next(true);
  const { data, error } = await getProjectContacts(projectId, assetId);

  if (data) {
    projectContactSubject.next(data);
    emitErrorState();
  }

  if (error) {
    projectContactSubject.next(defaultProjectContacts);
    emitErrorState(error.message);
  }
  projectContactLoadingSubject.next(false);
};

export const createProjectContact = async (contact: Omit<ProjectContact, 'id'>): Promise<ProjectContact | null> => {
  const { data, error } = await postProjectContacts(contact);
  if (data) {
    projectContactSubject.next([...projectContactSubject.getValue(), data]);
    return data;
  }

  if (error) {
    addError('project-contact', error.message);
  }

  return null;
};

export const deleteProjectContact = async (contactId: number): Promise<boolean> => {
  const { error } = await apiDeleteProjectContact(contactId);
  if (error) {
    addError('project-contact', error.message);
    return false;
  }
  return true;
};
